import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import axiosServices from "utils/axios";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { useAuth } from "context/AuthContext";
import { enterpriseTokens } from "styles/theme";

const VerifyOtp = () => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [loading, setLoading] = useState(false);
    const inputRefs = useRef([]);
    const dispatch = useDispatch();
    const { state } = useLocation();
    const navigate = useNavigate();
    const { getuserInfo } = useAuth();

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        const redirectPath = sessionStorage.getItem("postLoginRedirect");
        if (redirectPath) {
            sessionStorage.removeItem("postLoginRedirect");
            navigate(redirectPath, { replace: true });
        } else {
            navigate("/portfolio", { replace: true });
        }
    }, [navigate]);

    const openNotification = (msg, type = "error", duration = 6000) => {
        dispatch(openSnackbar({
            open: true,
            message: msg,
            variant: "alert",
            alert: { color: type, variant: "filled" },
            transition: "SlideUp",
        }));
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const handleChange = (event, index) => {
        const value = event.target.value;
        if (!/^\d?$/.test(value)) return;

        const nextOtp = [...otp];
        nextOtp[index] = value;
        setOtp(nextOtp);

        if (value && index < otp.length - 1) inputRefs.current[index + 1]?.focus();
    };

    const handleKeyDown = (event, index) => {
        if (event.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerifyOtp = async () => {
        if (!state?.email || !state?.password) {
            openNotification("Your login session has expired. Please sign in again.");
            navigate("/login");
            return;
        }

        setLoading(true);
        try {
            const response = await axiosServices.post(
                "Account/login",
                new URLSearchParams({
                    email: state.email,
                    password: state.password,
                    grant_type: "password",
                    otp: otp.join(""),
                }),
                { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
            );

            const token = response.data?.data?.accessToken;
            const redirectPath = sessionStorage.getItem("postLoginRedirect");
            if (!token) {
                openNotification(response.data?.message || "Invalid OTP");
                return;
            }

            openNotification("Welcome back! You've been logged in successfully.", "success");
            localStorage.setItem("token", token);
            getuserInfo(token);

            const roles = response?.data?.data?.roles || [];
            if (roles.includes("Customer")) {
                sessionStorage.removeItem("postLoginRedirect");
                navigate("/project-list", { replace: true });
            } else if (redirectPath) {
                sessionStorage.removeItem("postLoginRedirect");
                navigate(redirectPath, { replace: true });
            } else {
                navigate("/portfolio", { replace: true });
            }
        } catch (error) {
            openNotification(error?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const email = state?.email || "your registered email";
    const isComplete = otp.every(Boolean);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#EEF3F8",
                p: { xs: 2, sm: 3 },
            }}
        >
            <Box sx={{ width: "100%", maxWidth: 480 }}>
                <Box
                    sx={{
                        bgcolor: "#FFFFFF",
                        border: `1px solid ${enterpriseTokens.lineSoft}`,
                        borderRadius: "12px",
                        boxShadow: "0 18px 48px rgba(15, 23, 42, 0.10)",
                        p: { xs: 3, sm: 4 },
                    }}
                >
                    <IconButton onClick={() => navigate("/login")} aria-label="Back to login" sx={{ ml: -1, mb: 1, color: enterpriseTokens.primary }}>
                        <ArrowBackOutlinedIcon />
                    </IconButton>

                    <Typography sx={{ textAlign: "center", fontSize: 30, lineHeight: "38px", fontWeight: 850, color: enterpriseTokens.navy }}>
                        Verify your account
                    </Typography>
                    <Typography sx={{ textAlign: "center", mt: 1, fontSize: 14, lineHeight: "21px", color: enterpriseTokens.muted }}>
                        Enter the six-digit code sent to
                    </Typography>
                    <Typography sx={{ textAlign: "center", mt: 0.25, fontSize: 14, fontWeight: 800, color: enterpriseTokens.slate, overflowWrap: "anywhere" }}>
                        {email}
                    </Typography>

                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: { xs: 0.75, sm: 1 }, mt: 4 }}>
                        {otp.map((value, index) => (
                            <Box
                                component="input"
                                key={index}
                                ref={(element) => { inputRefs.current[index] = element; }}
                                inputMode="numeric"
                                aria-label={`OTP digit ${index + 1}`}
                                maxLength={1}
                                value={value}
                                onChange={(event) => handleChange(event, index)}
                                onKeyDown={(event) => handleKeyDown(event, index)}
                                sx={{
                                    width: "100%",
                                    minWidth: 0,
                                    height: { xs: 48, sm: 56 },
                                    boxSizing: "border-box",
                                    textAlign: "center",
                                    fontFamily: "Inter, Outfit, sans-serif",
                                    fontWeight: 800,
                                    fontSize: 22,
                                    color: enterpriseTokens.navy,
                                    bgcolor: "#F8FAFC",
                                    borderRadius: "8px",
                                    border: `1px solid ${value ? enterpriseTokens.primary : enterpriseTokens.line}`,
                                    outline: "none",
                                    "&:focus": {
                                        borderColor: enterpriseTokens.primary,
                                        boxShadow: "0 0 0 3px rgba(29, 78, 216, 0.12)",
                                    },
                                }}
                            />
                        ))}
                    </Box>

                    <Button onClick={handleVerifyOtp} variant="contained" fullWidth disabled={loading || !isComplete} sx={{ mt: 3.5, height: 48, fontSize: 15, fontWeight: 800 }}>
                        {loading ? <><CircularProgress size={19} sx={{ color: "inherit", mr: 1 }} />Verifying...</> : "Verify OTP"}
                    </Button>

                    <Typography sx={{ mt: 2.5, textAlign: "center", fontSize: 13, color: enterpriseTokens.muted }}>
                        Didn't receive the code? Check your spam folder or return to sign in.
                    </Typography>
                    <Button onClick={() => navigate("/login")} fullWidth sx={{ mt: 0.75, fontWeight: 750 }}>
                        Change email address
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default VerifyOtp;
